import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader } from "../components/UI";
import { User, Mail, ShieldCheck, Sparkles, Building, Users, Globe, Save } from "lucide-react";
import { toast } from "sonner";
import { detectBrowserTimezone } from "../lib/utils";

const ROLE_LABEL = {
  admin: "Admin",
  brand: "Brand Owner",
  manager: "Manager",
  rep: "Rep",
  user: "Rep (Legacy)",
};

const ROLE_DESC = {
  admin: "Full access across every organization and user.",
  brand: "Owns the organization. Sees every manager + rep under your brand.",
  manager: "Leads a team of reps. Sees your team's pipeline and visits.",
  rep: "Field user. Your accounts, your visits, your wins.",
  user: "Legacy / demo account — your own private workspace.",
};

// Common timezones, grouped for readability in the dropdown.
const TIMEZONE_OPTIONS = [
  { group: "United States", zones: [
    ["America/New_York", "Eastern (ET)"],
    ["America/Chicago", "Central (CT)"],
    ["America/Denver", "Mountain (MT)"],
    ["America/Phoenix", "Arizona (no DST)"],
    ["America/Los_Angeles", "Pacific (PT)"],
    ["America/Anchorage", "Alaska (AKT)"],
    ["Pacific/Honolulu", "Hawaii (HT)"],
  ]},
  { group: "Americas", zones: [
    ["America/Toronto", "Toronto"],
    ["America/Vancouver", "Vancouver"],
    ["America/Mexico_City", "Mexico City"],
    ["America/Sao_Paulo", "São Paulo"],
    ["America/Buenos_Aires", "Buenos Aires"],
  ]},
  { group: "Europe", zones: [
    ["Europe/London", "London"],
    ["Europe/Paris", "Paris"],
    ["Europe/Berlin", "Berlin"],
    ["Europe/Madrid", "Madrid"],
    ["Europe/Rome", "Rome"],
    ["Europe/Amsterdam", "Amsterdam"],
    ["Europe/Athens", "Athens"],
    ["Europe/Moscow", "Moscow"],
  ]},
  { group: "Africa & Middle East", zones: [
    ["Africa/Cairo", "Cairo"],
    ["Africa/Johannesburg", "Johannesburg"],
    ["Africa/Lagos", "Lagos"],
    ["Asia/Dubai", "Dubai"],
  ]},
  { group: "Asia & Pacific", zones: [
    ["Asia/Kolkata", "Kolkata / Mumbai"],
    ["Asia/Bangkok", "Bangkok"],
    ["Asia/Singapore", "Singapore"],
    ["Asia/Hong_Kong", "Hong Kong"],
    ["Asia/Shanghai", "Shanghai"],
    ["Asia/Tokyo", "Tokyo"],
    ["Asia/Seoul", "Seoul"],
    ["Australia/Sydney", "Sydney"],
    ["Australia/Perth", "Perth"],
    ["Pacific/Auckland", "Auckland"],
  ]},
  { group: "Other", zones: [["UTC", "UTC"]] },
];

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const initialTz = user?.timezone || "America/New_York";
  const [tz, setTz] = useState(initialTz);
  const [saving, setSaving] = useState(false);
  const dirty = tz !== initialTz;
  const browserTz = detectBrowserTimezone();
  const browserMatches = browserTz && tz === browserTz;
  const isDemo = user?.auth_type === "demo";

  const onSave = async () => {
    if (!dirty || isDemo) return;
    setSaving(true);
    const r = await updateProfile({ timezone: tz });
    setSaving(false);
    if (r.ok) toast.success("Timezone saved");
    else toast.error(r.error || "Couldn't save timezone");
  };

  const useBrowserTz = () => {
    if (browserTz) setTz(browserTz);
  };

  return (
    <div>
      <PageHeader
        testid="profile-header"
        title="Your profile"
        subtitle="Account, role, and workspace details."
      />
      <div className="card p-6 max-w-2xl" data-testid="profile-card">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-brand-ink text-white flex items-center justify-center">
            <User size={22} />
          </div>
          <div>
            <div className="font-display text-2xl text-brand-ink">{user?.name || "—"}</div>
            <div className="text-sm text-brand-mutedFg flex items-center gap-1">
              <Mail size={12} /> {user?.email || "—"}
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-6">
          <div className="rounded-xl border border-brand-border p-4">
            <div className="label">Role</div>
            <div className="mt-1 font-medium text-brand-ink">{ROLE_LABEL[user?.role] || user?.role}</div>
            <div className="text-xs text-brand-mutedFg mt-1">{ROLE_DESC[user?.role]}</div>
          </div>
          <div className="rounded-xl border border-brand-border p-4">
            <div className="label">Auth method</div>
            <div className="mt-1 flex items-center gap-2 text-brand-ink">
              {isDemo ? <Sparkles size={14}/> : <ShieldCheck size={14}/>}
              <span className="capitalize">{user?.auth_type || "—"}</span>
            </div>
          </div>
          <div className="rounded-xl border border-brand-border p-4" data-testid="profile-org">
            <div className="label">Organization</div>
            <div className="mt-1 flex items-center gap-2 text-brand-ink">
              <Building size={14} />
              <span>{user?.org_name || "—"}</span>
            </div>
          </div>
          <div className="rounded-xl border border-brand-border p-4" data-testid="profile-team">
            <div className="label">Team</div>
            <div className="mt-1 flex items-center gap-2 text-brand-ink">
              <Users size={14} />
              <span>{user?.team_name || "—"}</span>
            </div>
          </div>
        </div>

        {/* Timezone preference */}
        <div className="rounded-xl border border-brand-border p-4 mt-3" data-testid="profile-timezone-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="label flex items-center gap-1.5">
                <Globe size={12} /> Timezone
              </div>
              <p className="text-xs text-brand-mutedFg mt-1">
                Used for visit dates, invite expirations, and all timestamps shown across the app.
              </p>
            </div>
            {browserTz && !browserMatches && (
              <button
                type="button"
                onClick={useBrowserTz}
                className="btn-ghost text-xs"
                data-testid="use-browser-tz-button"
                title={`Use ${browserTz}`}
              >
                Use this device's tz ({browserTz})
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-[1fr,auto] gap-2 mt-3 items-stretch">
            <select
              data-testid="timezone-select"
              className="input-base"
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              disabled={isDemo}
            >
              {TIMEZONE_OPTIONS.map((g) => (
                <optgroup key={g.group} label={g.group}>
                  {g.zones.map(([id, label]) => (
                    <option key={id} value={id}>{label} — {id}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <button
              type="button"
              onClick={onSave}
              disabled={!dirty || saving || isDemo}
              className="btn-primary px-4"
              data-testid="save-timezone-button"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
          {isDemo && (
            <div className="text-xs text-brand-mutedFg mt-2">
              Demo accounts use the default (Eastern) timezone — sign in with a real account to change it.
            </div>
          )}
        </div>

        {isDemo && (
          <div className="mt-5 p-4 rounded-xl bg-brand-muted text-sm text-brand-ink2">
            You're in <strong>demo mode</strong> — feel free to poke around. Sign out and ask for an invite to keep your data permanently.
          </div>
        )}
      </div>
    </div>
  );
}
